import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPropertyCampaigns } from '../../../api/dashboard';

/**
 * "Sale info | Rental info" switch under a property's address.
 *
 * Christian Gravias, Monday 13245253503 (22 Oct 2026): "If its a rental and a
 * sale property its creating duplicates. We need to merge all of it into 1
 * profile and then have a button where you can click view rental info or view
 * sale info." The sale and the rental stay two records (own price, REA
 * listing, enquiries); this links them so the address reads as one property.
 * Hidden when the address has only one campaign.
 */
const LABELS = { sale: 'Sale info', rental: 'Rental info' };

// "2026-05-15" -> "May 2026". Own month names: en-AU gives "July" or "Jul" by engine.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthYear = (iso) => {
  const [y, m] = (iso || '').split('-').map(Number);
  return y && MONTHS[m - 1] ? `${MONTHS[m - 1]} ${y}` : '';
};

// Two sales of one house (14 Hakatere Street, Northcote, Christian Gravias
// 30 Oct 2026: "We sold it and then the new buyer wanted to sell again") read
// as "Sold · May 2026 | Off Market Listing · Jul 2026", oldest first.
const label = (c, sameKind) => {
  if (!sameKind || !c.status) return LABELS[c.kind] || c.kind;
  const when = monthYear(c.date);
  return when ? `${c.status} · ${when}` : `${LABELS[c.kind] || c.kind} · ${c.status}`;
};

const CampaignSwitch = ({ source, id }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    let live = true;
    setCampaigns([]);
    if (!id) return undefined;
    getPropertyCampaigns({ source, id })
      .then((res) => { if (live) setCampaigns(res.data?.campaigns || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [source, id]);

  if (campaigns.length < 2) return null;

  // Two sales (an old sold campaign and a new one) need their status to tell apart.
  const kindCount = campaigns.reduce((n, c) => ({ ...n, [c.kind]: (n[c.kind] || 0) + 1 }), {});

  return (
    <div
      role="group"
      aria-label="Campaigns at this address"
      className="inline-flex flex-wrap items-center gap-0.5 p-0.5 rounded-lg bg-gray-100 border border-gray-200 self-start"
    >
      {campaigns.map((c) => (
        <button
          key={`${c.source}-${c.id}`}
          type="button"
          aria-pressed={c.current}
          disabled={c.current}
          onClick={() => navigate(c.url)}
          className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-colors ${c.current
            ? 'bg-white text-[#374151] shadow-sm cursor-default'
            : 'text-gray-500 hover:text-teal-700 hover:bg-white/70'}`}
        >
          {label(c, kindCount[c.kind] > 1)}
        </button>
      ))}
    </div>
  );
};

export default CampaignSwitch;
